import { useCallback, RefObject, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { trackInternalEvent } from '@rio/tracking'
import snakeCase from 'lodash/snakeCase'
import { getEmptyFieldName, isEmptySection } from 'builder/modules/resumeEditor/aiProfileRules'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import {
  SectionNames,
  selectors,
  actions,
  AI_WORK_EXPERIENCE_ANALYTICS_LABEL,
} from 'builder/modules/resumeEditor'
import { useI18n } from 'builder/hooks/useI18n'
import { selectors as userSelectors } from 'builder/modules/user'
import { AiSummaryGenerateType } from 'builder/modules/constants'
import { useEscapeHandler } from 'builder/hooks/useEscapeHandler'
import { useClickOutside } from 'builder/hooks/useClickOutside'
import { useRemainingAiWorkExperienceAttempts } from 'builder/hooks/useRemainingAiWorkExperienceAttempts'
import { useClickOutsideExcept } from 'builder/hooks/useClickOutsideExcept'
import { AiKeywordsTogglerForPopups, useKeywords } from 'builder/components/AiFeatures/Keywords'
import {
  AddPreWrittenPhrasesButton,
  Divider,
} from 'builder/components/AiFeatures/AiProfileSummary/styles'
import { isTruthy } from 'builder/utils/richTextUtils'
import { Props } from '../types'
import { ANALYTICS_SECTION_NAME, DIALOG_ANALYTICS_LABEL } from '../constants'

export const useAiWorkExperience = (props: Props, container: RefObject<HTMLElement>) => {
  const { phrasesOpenerRef, editorContainerRef, onToggleSuggestions, currentWorkExperience } = props
  const { description, id: workExperienceId } = currentWorkExperience
  const resume = useTypedSelector(selectors.resume)
  const { i18n } = useI18n()
  const dispatch = useDispatch()
  const isPremiumUser = useTypedSelector(userSelectors.premiumAccess)
  const remainingAttempts = useRemainingAiWorkExperienceAttempts()
  const { isModalOpen } = useTypedSelector(selectors.aiWorkExperience)
  const isWorkExperienceEmpty = isEmptySection(resume, SectionNames.workExperiences)
  const emptyFieldName = getEmptyFieldName(SectionNames.workExperiences, currentWorkExperience)
  const isDescriptionEmpty = !isTruthy(description)
  const isLinkedToJobPosting = !!useTypedSelector(selectors.tailoredJobPostingId)
  const { isKeywordsAvailable } = useKeywords()
  const isImproveActive = useMemo(
    () => !emptyFieldName && !isDescriptionEmpty,
    [emptyFieldName, isDescriptionEmpty],
  )

  const infoText = useMemo(() => {
    if (emptyFieldName) {
      return i18n.t('builder.ai_work_experience.desc.no_data')
    } else if (isImproveActive) {
      return i18n.t('builder.ai_work_experience.desc.can_improve')
    } else {
      return i18n.t('builder.ai_work_experience.desc.can_generate')
    }
  }, [i18n, isImproveActive, emptyFieldName])

  const badgeText = useMemo(() => {
    if (remainingAttempts === Infinity) {
      return i18n.t('builder.ai_profile_summary.assistance_modal.unlimited')
    } else {
      return `${remainingAttempts} ${i18n.t('builder.ai_profile_summary.assistance_modal.left')}`
    }
  }, [i18n, remainingAttempts])

  const badgeTooltipText = useMemo(() => {
    if (isPremiumUser) {
      return i18n.t('builder.ai_profile_summary.tooltip.premium_user')
    } else {
      if (remainingAttempts === 0) {
        return i18n.t('builder.ai_profile_summary.tooltip.no_attempts_left')
      } else {
        return i18n.t('builder.ai_profile_summary.tooltip.free_user', {
          attempts_left: remainingAttempts,
        })
      }
    }
  }, [i18n, isPremiumUser, remainingAttempts])

  useClickOutsideExcept(container, [phrasesOpenerRef, editorContainerRef], () => {
    if (isModalOpen) {
      dispatch(actions.setIsAiWorkExperienceModalOpen(false))
    }
  })

  useEscapeHandler(() => dispatch(actions.setIsAiWorkExperienceModalOpen(false)))

  const handleGenerate = useCallback(() => {
    trackInternalEvent('click_generate', {
      label: DIALOG_ANALYTICS_LABEL,
      section: ANALYTICS_SECTION_NAME,
      description_empty: isDescriptionEmpty,
      experience_empty: isWorkExperienceEmpty,
    })

    dispatch(actions.setIsAiWorkExperienceModalOpen(false))

    if (!remainingAttempts) {
      dispatch(actions.setAiGenerationPaywallType('work_experience'))
      return
    }

    if (emptyFieldName) {
      dispatch(
        actions.highlightWorkExperienceCardEmptyField({
          cardId: currentWorkExperience.cid || currentWorkExperience.id,
          emptyFieldName,
          tooltipData: {
            title: i18n.t(
              `builder.ai_work_experience.empty_field_highlight_tooltip.titles.${snakeCase(
                emptyFieldName,
              )}`,
            ),
            content: i18n.t('builder.ai_work_experience.empty_field_highlight_tooltip.description'),
            buttonLabel: i18n.t(
              'builder.ai_work_experience.empty_field_highlight_tooltip.button_text',
            ),
          },
        }),
      )
      return
    }

    dispatch(actions.setAiWorkExperienceActionType(AiSummaryGenerateType.generate))

    if (isLinkedToJobPosting) {
      dispatch(actions.setIsWorkExperienceWithTailoredResumeModalOpen(true))
    } else {
      dispatch(actions.generateResumeAiWorkExperience({ workExperienceId }))

      trackInternalEvent('start_generating', {
        section: ANALYTICS_SECTION_NAME,
        label: AI_WORK_EXPERIENCE_ANALYTICS_LABEL,
        description_empty: isDescriptionEmpty,
        mode: AiSummaryGenerateType.generate,
      })
    }
  }, [
    isDescriptionEmpty,
    isWorkExperienceEmpty,
    dispatch,
    remainingAttempts,
    emptyFieldName,
    isLinkedToJobPosting,
    currentWorkExperience.cid,
    currentWorkExperience.id,
    i18n,
    workExperienceId,
  ])

  const handleImprove = useCallback(() => {
    dispatch(actions.setIsAiWorkExperienceModalOpen(false))

    if (remainingAttempts > 0) {
      dispatch(actions.setAiWorkExperienceActionType(AiSummaryGenerateType.improve))
      if (isLinkedToJobPosting) {
        dispatch(actions.setIsIncludeJobPostingToAiWorkExperience(true))
        dispatch(actions.setIsAiWorkExperienceWithTailoredResumeModalOpen(true))
      } else {
        dispatch(actions.generateResumeAiWorkExperience({ workExperienceId }))

        trackInternalEvent('start_generating', {
          section: ANALYTICS_SECTION_NAME,
          label: AI_WORK_EXPERIENCE_ANALYTICS_LABEL,
          description_empty: isDescriptionEmpty,
          mode: AiSummaryGenerateType.improve,
        })
      }
    } else {
      dispatch(actions.setAiGenerationPaywallType('work_experience'))
    }

    trackInternalEvent('click_improve', {
      section: ANALYTICS_SECTION_NAME,
      label: DIALOG_ANALYTICS_LABEL,
      description_empty: isDescriptionEmpty,
      experience_empty: isWorkExperienceEmpty,
    })
  }, [
    remainingAttempts,
    isDescriptionEmpty,
    isWorkExperienceEmpty,
    dispatch,
    isLinkedToJobPosting,
    workExperienceId,
  ])

  const renderPrewrittenAction = () => {
    return (
      <AddPreWrittenPhrasesButton
        onClick={() => {
          onToggleSuggestions({ trigger: 'click' })
          trackInternalEvent('click_add_phrases', {
            section: ANALYTICS_SECTION_NAME,
            label: DIALOG_ANALYTICS_LABEL,
            description_empty: isDescriptionEmpty,
            experience_empty: isWorkExperienceEmpty,
          })
        }}
      >
        {i18n.t('builder.ai_profile_summary.option.add_prewritten_phrases')}
      </AddPreWrittenPhrasesButton>
    )
  }

  const renderKeywordsAction = () => {
    if (!isKeywordsAvailable) return null

    return (
      <>
        <Divider />

        <AiKeywordsTogglerForPopups
          section={SectionNames.workExperiences}
          text={i18n.t('builder.ai_profile_summary.assistance_modal.add_keywords')}
          onClick={() => dispatch(actions.setIsAiWorkExperienceModalOpen(false))}
        />
      </>
    )
  }

  return {
    badgeText,
    badgeTooltipText,
    infoText,
    isImproveActive,
    useClickOutside,
    handleGenerate,
    handleImprove,
    renderPrewrittenAction,
    isWorkExperienceEmpty,
    remainingAttempts,
    renderKeywordsAction,
    isDescriptionEmpty,
  }
}
